import ExcelJS, { Worksheet, Workbook } from 'exceljs';
import { set, zip, concat } from 'lodash';
import MessageBus from '@/Utils/message/message-bus';
import { dateFormat } from '@/Utils/date-format';
import excelMap from './source';
import { removeDuplicates } from '@/Utils/average';

/**
 *读取配置
 */
interface LoadOption {
    /**
     * 验证数组
     */
    verification: Array<string | null>;
    /**
     * 映射信息
     */
    mapInfo: Array<{
        condition?: Array<{ position: { row: number; column: number }; value: string | number }>;
        filedMap: Array<{ path: Array<string | number>; position: { row: number; column: number }; type?: 'date' }>;
    }>;
}

/**
 *  工具类，加载数据、上传数据请写在此处
 *
 */
export default class Tools {
    /**
     * map
     */
    static map = excelMap;
    /**
     * @author guan
     * @description 初始化模板
     * @return void
     * @param type
     */
    static initTemplate(type: string): Record<string, string> {
        return Tools.map[type];
    }

    /**
     * 特殊字符
     */
    static specialCharacters = ['*', '?', ':', '/', '\\', '[', ']'];
    /**
     * @author guan
     * @description 初始化excel 文件
     * @return void
     * @param fileName 文件名
     * @param type 类型
     * @param transposition 是否转置
     * @param initData 初始化处理数据
     */
    static initXlsxFile(
        fileName: string,
        type: string,
        //eslint-disable-next-line
        initData?: any,
        transposition?: (Worksheet: Worksheet) => Promise<Worksheet>,
    ): void {
        const dictList = Tools.initTemplate(type);
        if (dictList == null) {
            throw new Error('导出文件模板不存在');
        }
        const tHeader = Object.keys(dictList);
        const filterVal = tHeader.map((x) => dictList[x]);
        if (transposition) {
            /** 需要转置的时候，调用另外的处理方法 */
            Tools.init(fileName, tHeader, undefined, transposition).catch((e) => e);
        } else {
            if (initData) {
                const data = Tools.formatJson(filterVal, initData);
                Tools.init(fileName, tHeader, data).catch((e) => e);
            }
        }
    }

    /**
     * @author guan
     * @description 初始化excel 数据
     * @return void
     * @param fileName 文件名
     * @param type 类型
     * @param initData 初始化处理数据
     */
    static initXlsxData(
        fileName: string,
        type: string,
        //eslint-disable-next-line
        initData?: any,
    ): { tHeader: string[]; data: string[][] } {
        const dictList = Tools.initTemplate(type);
        const tHeader = Object.keys(dictList);
        if (Object.keys(initData).length > 0) {
            const filterVal = tHeader.map((x) => dictList[x]);
            const data = Tools.formatJson(filterVal, initData);
            return { tHeader: tHeader, data: data };
        } else {
            return { tHeader: tHeader, data: [] };
        }
    }

    /**
     * 简单excel导出
     */
    static async exportXlsxFile(fileName: string, sheets: Array<{ name?: string; data: unknown[][] }>): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        /** 添加工作簿创建者 */
        workbook.creator = 'idpdp';
        /** 创建时间 */
        workbook.created = new Date();
        workbook.properties.date1904 = true;
        workbook.views = [
            {
                x: 0,
                y: 0,
                width: 100,
                height: 100,
                firstSheet: 0,
                activeTab: 1,
                visibility: 'visible',
            },
        ];

        sheets.forEach((sheet) => {
            if (sheet.data.length > 1) {
                const worksheet = workbook.addWorksheet();
                if (sheet.name != null) {
                    worksheet.name = sheet.name;
                }
                sheet.data.forEach((rowData) => worksheet.addRow(rowData));
                worksheet.columns.forEach((column) => (column.width = 25));
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        Tools.download(buffer, fileName);
    }

    /**
     * @author guan
     * @description 初始化工簿
     * @param sheetName  工作簿名称
     * @param Header 表头
     * @param data 数据data，缺失時必须传入回调函数
     * @param initStyle 回调，需要生成特殊格式，在具体的组件中添加回调
     * @return Promise<void> 异步加载
     */
    static async init(sheetName: string, Header?: string[], data?: string[][], initStyle?: (Worksheet: Worksheet) => Promise<Worksheet>): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        /** 添加工作簿创建者 */
        workbook.creator = 'idpdp';
        /** 创建时间 */
        workbook.created = new Date();
        /** 将工作簿日期设置为 1904 年日期系统 */
        workbook.properties.date1904 = true;
        workbook.views = [
            {
                x: 0,
                y: 0,
                width: 100,
                height: 100,
                firstSheet: 0,
                activeTab: 1,
                visibility: 'visible',
            },
        ];
        if (this.specialCharacters.find((x) => sheetName.includes(x))) {
            const name = sheetName.split('-')[0];
            sheetName = name + '-' + '生产厂商';
        }
        const sheet = workbook.addWorksheet(sheetName);
        /** 自定义，不从简单模板走 */
        if (initStyle) {
            initStyle(sheet)
                .then(async () => {
                    const buffer = await workbook.xlsx.writeBuffer();
                    Tools.download(buffer, sheetName);
                })
                .catch((e) => e);
        } else {
            if (Header) {
                sheet.columns = Header.map((x) => {
                    return {
                        header: x,
                        key: x,
                        width: 18,
                        style: { alignment: { horizontal: 'center', vertical: 'middle' } },
                    };
                });
            } else
                sheet.columns = [
                    { header: 'Id', key: 'id', width: 16 },
                    { header: 'Name', key: 'name', width: 16 },
                    { header: 'D.O.B.', key: 'DOB', width: 16, outlineLevel: 1 },
                ];
            if (data) {
                data.forEach((x, index) => {
                    const row = sheet.getRow(index + 2);
                    row.values = x;
                });
            }
            const buffer = await workbook.xlsx.writeBuffer();
            Tools.download(buffer, sheetName);
        }
    }

    /**
     * MultipleSheet
     *
     * @author dev_guan
     * @description 初始化工簿
     * @param sheetName  工作簿名称
     * @return Promise<void> 异步加载
     */
    static async initMultipleSheet(fileName: string, sheets: string[], initStyle?: (Workbook: Workbook) => Promise<Workbook>): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        /** 添加工作簿创建者 */
        workbook.creator = 'idpdp';
        /** 创建时间 */
        workbook.created = new Date();
        /** 将工作簿日期设置为 1904 年日期系统 */
        workbook.properties.date1904 = true;
        workbook.views = [
            {
                x: 0,
                y: 0,
                width: 100,
                height: 100,
                firstSheet: 0,
                activeTab: 1,
                visibility: 'visible',
            },
        ];
        sheets.forEach((x) => {
            workbook.addWorksheet(x);
        });

        if (initStyle) {
            initStyle(workbook)
                .then(async () => {
                    const buffer = await workbook.xlsx.writeBuffer();
                    Tools.download(buffer, fileName);
                })
                .catch((e) => e);
        } else {
            const buffer = await workbook.xlsx.writeBuffer();
            Tools.download(buffer, fileName);
        }
    }

    /**
     * @author guan
     * @description 初始化工簿
     * @param sheetName  工作簿名称
     * @param Header 表头
     * @param data 数据data，缺失時必须传入回调函数
     * @param initStyle 回调，需要生成特殊格式，在具体的组件中添加回调
     * @return Promise<void> 异步加载
     */
    static async initSheets(sheetName: string, data: string[][][], typicalDay: string[], header?: string[]): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        /** 添加工作簿创建者 */
        workbook.creator = 'idpdp';
        /** 创建时间 */
        workbook.created = new Date();
        /** 将工作簿日期设置为 1904 年日期系统 */
        workbook.properties.date1904 = true;
        workbook.views = [
            {
                x: 0,
                y: 0,
                width: 100,
                height: 100,
                firstSheet: 0,
                activeTab: 1,
                visibility: 'visible',
            },
        ];

        if (header && header.length > 0) {
            /** 新增工作表 */
            data.forEach((value, key) => {
                const sheet = workbook.addWorksheet(typicalDay[key]);
                if (value.length === 0) {
                    return;
                }
                sheet.columns = header.map((x) => {
                    return {
                        header: x,
                        key: x,
                        width: 18,
                        style: { alignment: { horizontal: 'center', vertical: 'middle' } },
                    };
                });
                sheet.addRows(value);
            });
        } else {
            const list = data[0].map((x) => {
                return Object.keys(x);
            });
            /** 新增工作表 */
            data.forEach((value, key) => {
                const sheet = workbook.addWorksheet(typicalDay[key]);
                if (value.length === 0) {
                    return;
                }
                sheet.columns = list[0].map((x) => {
                    return {
                        header: x,
                        key: x,
                        width: 18,
                        style: { alignment: { horizontal: 'center', vertical: 'middle' } },
                    };
                });
                sheet.addRows(value);
            });
        }

        const buffer = await workbook.xlsx.writeBuffer();
        Tools.download(buffer, sheetName);
    }

    /**
     * @author guan
     * @description 下载接口
     * @return void
     * @param buffer 接受一个buffer源
     * @param title 下载标题
     */
    static download(buffer: BufferSource, title: string): void {
        const file = new File([buffer], `${title}.xlsx`);
        const blobURL = window.URL.createObjectURL(file);
        // 创建a标签，用于跳转至下载链接
        const tempLink = document.createElement('a');
        tempLink.style.display = 'none';
        tempLink.href = blobURL;
        tempLink.setAttribute('download', decodeURI(file.name));
        // 兼容：某些浏览器不支持HTML5的download属性
        if (typeof tempLink.download === 'undefined') {
            tempLink.setAttribute('target', '_blank');
        }
        // 挂载a标签
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
        // 释放blob URL地址
        window.URL.revokeObjectURL(blobURL);
    }

    /**
     * userUpload
     */
    static async userUpload(): Promise<File | null> {
        return new Promise<File | null>((resolve) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.oninput = () => {
                if (input.files != null) {
                    resolve(input.files[0]);
                } else {
                    resolve(null);
                }
                input.remove();
            };
            input.click();
        });
    }
    /**
     * @author guan
     * @description 在路径为对象，拥有特殊处理方式
     * @return void
     */
    static handleData(j: { route: string; deal: (data) => string }, jsonData: Record<string, unknown>): string {
        const router = j.route.split('.');
        let value;
        router.forEach((x, y) => {
            if (y === 0) {
                value = jsonData[x];
            } else {
                value = value[x];
            }
        });
        return j.deal(value);
    }
    /**
     * @author guan
     * @description 路径解析
     * @return void
     */
    //eslint-disable-next-line
    static formatJson(filterVal: string[], jsonData: any): string[][] {
        const data = filterVal.map((j) => {
            if (typeof j === 'object') {
                return Tools.handleData(j, jsonData);
            } else {
                const router = j.split('.');
                let value;
                router.forEach((x, y) => {
                    if (y === 0) {
                        value = jsonData[x];
                    } else {
                        value = value[x];
                    }
                });
                return [value];
            }
        });
        return zip(...data);
    }

    /**
     * 从Excel文件读取数据, 单sheet
     */
    static async loadDataByExcel(file: File, verification?: Array<string | null>): Promise<string[][]> {
        /**
         * 格式化数据
         */
        function formatTimeString(testField: unknown): unknown {
            if ((testField as object) instanceof Date) {
                const newDate = testField as Date;
                newDate.setHours(newDate.getHours() - 8);
                return dateFormat('hh:MM', newDate);
            }
            return testField;
        }
        const result = new Array<string[]>();
        const workbook = new ExcelJS.Workbook();
        /**
         * arrayBuffer 不适合处理大数据量文件，会一次性占满整个浏览器内存导致浏览器崩溃
         * 建议后台处理该部分上传，下载excel逻辑
         */
        await workbook.xlsx.load(await file.arrayBuffer());

        //库的问题, 不遍历一遍列,下面遍历行的时候就会忽略掉空单元格,我也不知道为什么
        workbook.worksheets[0].columns.forEach((item) => {
            item.values;
        });
        workbook.worksheets[0].eachRow((row) => {
            if (row.model) {
                if (row.model.cells) {
                    result.push(
                        row.model.cells.map((cell) => {
                            if (cell.result != null) {
                                return formatTimeString(cell.result) as string;
                            } else if (cell.value != null) {
                                return formatTimeString(cell.value) as string;
                            } else {
                                return '';
                            }
                        }),
                    );
                }
            }
        });
        if (verification != null) {
            let isPassesValidation = true;
            verification.forEach((item, index) => {
                if (item === null) {
                    return;
                }
                if (!String(result[0][index]).includes(item)) {
                    isPassesValidation = false;
                }
            });
            if (!isPassesValidation) {
                // MessageBus.Notification('error', { description: 'excel格式不正确', message: '提示' });
            }
        }
        return result;
    }

    /**
     * 从Excel文件读取数据, 单sheet，自定义负荷模块数据处理
     */
    static async loadDataByExcelByDate(file: File, verification?: Array<string | null>): Promise<string[][]> {
        /**
         *
         */
        function formatTimeString(testField: unknown): unknown {
            if ((testField as object) instanceof Date) {
                const newDate = testField as Date;
                newDate.setHours(newDate.getHours() - 8);
                return dateFormat('yy-mm-dd hh:MM:ss', newDate);
            }
            return testField;
        }
        const result = new Array<string[]>();
        const workbook = new ExcelJS.Workbook();
        /**
         * arrayBuffer 不适合处理大数据量文件，会一次性占满整个浏览器内存导致浏览器崩溃
         *
         * 建议后台处理该部分上传，下载excel逻辑
         */
        await workbook.xlsx.load(await file.arrayBuffer());

        //库的问题, 不遍历一遍列,下面遍历行的时候就会忽略掉空单元格,我也不知道为什么
        workbook.worksheets[0].columns.forEach((item) => {
            item.values;
        });
        workbook.worksheets[0].eachRow((row) => {
            if (row.model) {
                if (row.model.cells) {
                    result.push(
                        row.model.cells.map((cell) => {
                            if (cell.result != null) {
                                return formatTimeString(cell.result) as string;
                            } else if (cell.value != null) {
                                return formatTimeString(cell.value) as string;
                            } else {
                                return '';
                            }
                        }),
                    );
                }
            }
        });
        if (verification != null) {
            let isPassesValidation = true;
            verification.forEach((item, index) => {
                if (item === null) {
                    return;
                }
                if (!String(result[0][index]).includes(item)) {
                    isPassesValidation = false;
                }
            });
            if (!isPassesValidation) {
                throw new Error('excel格式不正确');
            }
        }
        return result;
    }

    /**
     * 从Excel文件读取数据, 多sheets
     */
    static async loadDataByExcelMultipleSheets(file: File): Promise<string[][]> {
        /**
         *
         */
        function formatTimeString(testField: unknown): unknown {
            if ((testField as object) instanceof Date) {
                const newDate = testField as Date;
                newDate.setHours(newDate.getHours() - 8);
                return dateFormat('hh:MM', newDate);
            }
            return testField;
        }
        const result = new Array<string[]>();
        const workbook = new ExcelJS.Workbook();
        /**
         * arrayBuffer 不适合处理大数据量文件，会一次性占满整个浏览器内存导致浏览器崩溃
         * 建议后台处理该部分上传，下载excel逻辑
         */
        await workbook.xlsx.load(await file.arrayBuffer());
        workbook.eachSheet((worksheet) => {
            //库的问题, 不遍历一遍列,下面遍历行的时候就会忽略掉空单元格,我也不知道为什么
            worksheet.columns.forEach((item) => {
                item.values;
            });
            worksheet.eachRow((row) => {
                if (row.model) {
                    if (row.model.cells) {
                        result.push(
                            row.model.cells.map((cell) => {
                                if (cell.result != null) {
                                    return formatTimeString(cell.result) as string;
                                } else if (cell.value != null) {
                                    return formatTimeString(cell.value) as string;
                                } else {
                                    return '';
                                }
                            }),
                        );
                    }
                }
            });
            return result;
        });
        return result;
    }

    /**
     *根据配置从excel读取数据并转换成需要的字典
     */
    static async loadDataFromExcelByOption(file: File, option: LoadOption): Promise<unknown[]> {
        const data = await this.loadDataByExcel(file);
        const verifyFields = data.shift() as string[];
        return data.map((rowData) => this.loadRowDataFromExcelByOption(rowData, verifyFields, option));
    }

    /**
     *
     */
    static loadRowDataFromExcelByOption(data: string[], verifyFields: string[], option: LoadOption): object {
        const result = {};
        let isPassesValidation = true;
        option.verification.forEach((item, index) => {
            if (item === null) {
                return;
            }
            if (item !== verifyFields[index]) {
                isPassesValidation = false;
            }
        });
        if (!isPassesValidation) {
            MessageBus.Notification('warning', { description: 'excel格式不正确', message: '提示' });
            throw new Error('excel格式不正确');
        }
        const mapInfo = option.mapInfo.find((item) => {
            if (item.condition == null) {
                return true;
            } else {
                return item.condition
                    .map((item) => {
                        return data[item.position.column] === item.value;
                    })
                    .reduce((item, isSatisfyTheCondition) => {
                        return item && isSatisfyTheCondition;
                    });
            }
        });
        if (mapInfo == null) {
            MessageBus.Notification('warning', { description: '格式不正确', message: '提示' });
            throw new Error('excel格式不正确');
        }
        mapInfo.filedMap.forEach((item) => {
            let cellData: unknown;
            if (item.type === 'date') {
                cellData = new Date(data[item.position.column]);
            } else {
                cellData = data[item.position.column];
            }
            set(result, item.path, cellData);
        });
        MessageBus.Notification('success', { message: '成功上传数据!', description: '' });
        return result;
    }

    /**
     *让浏览器下载文件
     */
    static downloadFile(file: File): void {
        const blobURL = window.URL.createObjectURL(file);
        const tempLink = document.createElement('a');
        tempLink.style.display = 'none';
        tempLink.href = blobURL;
        tempLink.setAttribute('download', decodeURI(file.name));
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
    }

    /**
     * 生成优化方案数据
     */
    static async initNewSheets(title: string, data: string[][], header?: string[]): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        /** 添加工作簿创建者 */
        workbook.creator = 'idpdp';
        /** 创建时间 */
        workbook.created = new Date();
        /** 将工作簿日期设置为 1904 年日期系统 */
        workbook.properties.date1904 = true;
        workbook.views = [
            {
                x: 0,
                y: 0,
                width: 100,
                height: 100,
                firstSheet: 0,
                activeTab: 1,
                visibility: 'visible',
            },
        ];
        if (!header) {
            const sheetsName = removeDuplicates(
                data.map((item) => {
                    return item[0]['元件自定义名称'];
                }),
            );
            const list = data[0].map((x) => {
                return Object.keys(x);
            });
            /** 新增工作表 */
            data.forEach((value, key) => {
                const sheetName = sheetsName[key];
                const sheet = workbook.addWorksheet(sheetName);
                const header = concat(['日期'], list[0].slice(0, -1));
                sheet.columns = header.map((x) => {
                    return {
                        header: x,
                        key: x,
                        width: 18,
                        style: { alignment: { horizontal: 'center', vertical: 'middle' } },
                    };
                });
                sheet.addRows(value);
            });
        } else {
            const sheet = workbook.addWorksheet();
            sheet.columns = header.map((x) => {
                return {
                    header: x,
                    key: x,
                    width: 18,
                    style: { alignment: { horizontal: 'center', vertical: 'middle' } },
                };
            });
            sheet.addRows(data);
        }

        const buffer = await workbook.xlsx.writeBuffer();
        Tools.download(buffer, title);
    }
}
